import { useMutation, useQueryClient } from '@tanstack/react-query'
import { authService, dataSource } from '../../service/domain/_service.config'
import { ACTIVITY_LOGS_QUERY_KEY } from '../activity/useActivityLogs'
import { setActiveChallengeId } from '../challenge/activeChallenge'
import { ENTITLEMENT_QUERY_KEY } from '../membership/useChallengeAccess'
import { TEAM_MEMBERSHIPS_QUERY_KEY } from '../membership/useTeamMemberships'
import { TEAM_QUERY_KEY } from './useTeam'
import { TEAMS_QUERY_KEY } from './useTeams'

export function useJoinTeam() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (teamID: string) => {
      const authID = await authService.getCurrentUserAuthID()
      if (!authID) throw new Error('No authenticated user found')

      const user = await dataSource.getUserByAuthID(authID)
      if (!user) throw new Error('No user found')

      const team = await dataSource.getTeam(teamID)
      if (!team) throw new Error('Team not found')

      const challenge = await dataSource.getChallenge(team.challengeID)
      const teamSizeMax = challenge.teamSizeMax

      const userPaid = await dataSource.isUserEntitled(user.id, team.challengeID)
      const teamPaid = team.hasPaid === true

      if (!userPaid && !teamPaid) {
        throw new Error(
          'Payment required: You must pay for an individual pass or join a team that has paid for a team pass',
        )
      }

      const existing = await dataSource.getTeamMembershipForChallenge(user.id, team.challengeID)
      if (existing && existing.teamID !== teamID) {
        throw new Error('Already on a team in this challenge')
      }

      if (team.isFull(teamSizeMax) && !team.hasMember(user.id)) {
        throw new Error('Team is full')
      }

      await dataSource.addTeamMember(user.id, teamID, team.challengeID)

      return { teamID, challengeID: team.challengeID, userID: user.id }
    },
    onSuccess: ({ teamID, challengeID, userID }) => {
      setActiveChallengeId(challengeID)
      queryClient.invalidateQueries({ queryKey: TEAMS_QUERY_KEY(challengeID) })
      queryClient.invalidateQueries({ queryKey: TEAM_QUERY_KEY(teamID) })
      queryClient.invalidateQueries({ queryKey: TEAM_MEMBERSHIPS_QUERY_KEY(userID) })
      queryClient.invalidateQueries({
        queryKey: ENTITLEMENT_QUERY_KEY(userID, challengeID),
      })
      queryClient.invalidateQueries({ queryKey: ACTIVITY_LOGS_QUERY_KEY(teamID) })
    },
  })
}
